import { useState, useEffect } from "react";
import { getGoogleClientId } from "@pages/LoginManagement/api.ts";

/**
 * Custom hook for handling Google Identity Services token retrieval.
 * Manages the loading and rendering of Google Sign-In button and token callback.
 */
export const useGoogleToken = () => {
  const [waitingForGoogleToken, setWaitingForGoogleToken] = useState(false);
  const [googleTokenCallback, setGoogleTokenCallback] = useState<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);

  /**
   * Requests a Google ID token by setting up the callback and showing the sign-in button.
   * @returns Promise that resolves with the Google ID token
   */
  const requestGoogleToken = (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      setGoogleTokenCallback({ resolve, reject });
      setWaitingForGoogleToken(true);
    });
  };

  /**
   * Cancels the Google token request and cleans up state.
   */
  const cancelGoogleToken = () => {
    setWaitingForGoogleToken(false);
    setGoogleTokenCallback(null);
    const container = document.getElementById("google-signin-button-container");
    if (container) {
      container.innerHTML = "";
    }
  };

  // Initialize Google Identity Services and render button when waiting for token
  useEffect(() => {
    if (!waitingForGoogleToken || !googleTokenCallback) return;

    const initializeGoogleSignIn = async () => {
      // Load Google Identity Services script if not already loaded
      const loadGoogleScript = (): Promise<void> => {
        return new Promise((resolveScript, rejectScript) => {
          // Check if Google Identity Services is already loaded
          if ((window as any).google?.accounts?.id) {
            resolveScript();
            return;
          }

          const script = document.createElement("script");
          script.src = "https://accounts.google.com/gsi/client";
          script.async = true;
          script.defer = true;
          script.onload = () => resolveScript();
          script.onerror = () =>
            rejectScript(new Error("Failed to load Google Identity Services"));
          document.head.appendChild(script);
        });
      };

      try {
        await loadGoogleScript();

        // Wait for script to be fully ready
        let attempts = 0;
        const maxAttempts = 50;
        const waitForGoogle = setInterval(() => {
          attempts++;
          if ((window as any).google?.accounts?.id) {
            clearInterval(waitForGoogle);
            renderGoogleButton();
          } else if (attempts >= maxAttempts) {
            clearInterval(waitForGoogle);
            if (googleTokenCallback) {
              googleTokenCallback.reject(
                new Error("Google Identity Services failed to load")
              );
              setGoogleTokenCallback(null);
              setWaitingForGoogleToken(false);
            }
          }
        }, 100);
      } catch (error) {
        if (googleTokenCallback) {
          googleTokenCallback.reject(error as Error);
          setGoogleTokenCallback(null);
          setWaitingForGoogleToken(false);
        }
      }
    };

    const renderGoogleButton = async () => {
      try {
        const clientId = await getGoogleClientId();
        if (!clientId) {
          throw new Error("Google Client ID not configured");
        }

        const container = document.getElementById(
          "google-signin-button-container"
        );
        if (!container) return;

        // Clear container
        container.innerHTML = "";

        // Initialize Google Identity Services with callback
        (window as any).google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential: string }) => {
            if (googleTokenCallback) {
              googleTokenCallback.resolve(response.credential);
              setGoogleTokenCallback(null);
              setWaitingForGoogleToken(false);
            }
          },
        });

        // Render the Google Sign-In button
        (window as any).google.accounts.id.renderButton(container, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: "100%",
        });
      } catch (error) {
        if (googleTokenCallback) {
          googleTokenCallback.reject(error as Error);
          setGoogleTokenCallback(null);
          setWaitingForGoogleToken(false);
        }
      }
    };

    initializeGoogleSignIn();

    // Cleanup function
    return () => {
      const container = document.getElementById(
        "google-signin-button-container"
      );
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [waitingForGoogleToken, googleTokenCallback]);

  return {
    waitingForGoogleToken,
    requestGoogleToken,
    cancelGoogleToken,
  };
};
